import { SvgIconComponent } from '@mui/icons-material';
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from '@mui/material';
import { ArrowDropDownIcon } from '@mui/x-date-pickers/icons';
import React, { FC } from 'react';

interface FilterWithCheckboxProps {
  dropdownOptions: {
    id: string;
    label: string;
    hideCheckbox?: boolean;
    menuItemDisabled?: boolean;
    showBorderAfter?: boolean;
  }[];
  label?: string;
  handleChange: (event: SelectChangeEvent<string[]>) => void;
  selectedOptions: string[];
  icon?: SvgIconComponent;
  wrapperSx?: Object;
  MenuProps?: Object;
  disabled?: boolean;
  onClose?: () => void;
  renderValue?: (selected: string[]) => React.ReactNode;
}

const FilterWithCheckbox: FC<FilterWithCheckboxProps> = ({
  dropdownOptions,
  handleChange,
  selectedOptions,
  icon,
  wrapperSx,
  MenuProps,
  disabled,
  label,
  onClose,
  renderValue,
}) => {
  return (
    <FormControl fullWidth>
      {label && (
        <InputLabel
          id={`${label}-label`}
          sx={{
            fontSize: '12px',
            color: '#6C6C6C',
            fontWeight: 500,
            backgroundColor: '#FFFFFF',
            marginTop: '2px',
            paddingX: '5px',
            width: '100%',
            '&:hover': {
              color: '#6C6C6C',
            },
            '&.Mui-focused': {
              color: '#6C6C6C',
            },
          }}
        >
          {label}
        </InputLabel>
      )}
      <Select
        multiple
        label={label}
        labelId={`${label}-label`}
        onChange={handleChange}
        value={selectedOptions || []}
        onClose={onClose}
        IconComponent={icon ? icon : ArrowDropDownIcon}
        MenuProps={MenuProps}
        renderValue={
          renderValue
            ? renderValue
            : (selected) =>
                dropdownOptions
                  .filter((option) => selected.includes(option.id))
                  .map((option) => option.label)
                  .join(', ')
        }
        sx={{
          ...{
            boxShadow: 'none',
            '.MuiOutlinedInput-notchedOutline': { borderStyle: 'none' },
            '& .MuiSelect-select': {
              padding: 0,
            },

            '.MuiSvgIcon-root ': {
              fill: '#2F736E !important',
            },
          },
          '.MuiSvgIcon-root ': {
            fill: '#2F736E !important',
          },
          borderRadius: '8px',
          border: '1px solid #E8EAEB',
          paddingY: '0.375rem',
          paddingLeft: '1rem',
          paddingRight: '0.75rem',
          fontSize: '0.875rem',
          lineHeight: '1.25rem',
          letterSpacing: '-0.35px',
          fontWeight: 700,
          color: '#6C6C6C',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '48px',
          ...wrapperSx,
        }}
        disabled={disabled}
      >
        {dropdownOptions.map((option) => (
          <MenuItem
            key={option.id}
            value={option.id}
            sx={{
              fontSize: '14px',
              fontWeight: 600,
              color: '#162936',
              height: '40px',
              padding: '0.5rem 0.75rem',
              ...(option.menuItemDisabled && {
                opacity: 0.5,
                pointerEvents: 'none',
              }),
              ...(option.showBorderAfter && {
                borderBottom: '1px solid #E8EAEB',
              }),
              '&.Mui-selected': {
                backgroundColor: 'transparent',
              },
            }}
          >
            <FormControlLabel
              control={
                !option.hideCheckbox ? (
                  <Checkbox
                    sx={{ color: '#2F736E', fontSize: '18px' }}
                    checked={selectedOptions.includes(option.id)}
                    inputProps={{ 'aria-label': `Filter by ${label}` }}
                  />
                ) : (
                  <Box padding={'0.35rem 0.25rem'} />
                )
              }
              label={
                <Typography
                  fontSize="0.875rem"
                  whiteSpace="nowrap"
                  lineHeight="1.25rem"
                  fontWeight={500}
                  color="#162936"
                >
                  {option.label}
                </Typography>
              }
            />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default FilterWithCheckbox;
